import FacetEditModal from '@UI/modals/FacetEditModal';
import ModalErrorBoundary from '@UI/modals/ModalErrorBoundary';
import type { Facet } from '@generalTypes/apiTypes';
import { Modal } from '@kathondvla/react-shared-components/src/components';
import { getAngularService } from '@kathondvla/react-shared-components/src/helpers/angularReactHelper';
import { loadReferenceFrame } from '@newStore/referenceFrames/referenceFramesState';
import type { EditAsideWebFacets } from '@nodeTypeConfig/configTypes';
import { updateWebsiteConfigurationActionNew } from '@store/actions/documentActions';
import { any } from 'prop-types';
import type React from 'react';
import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useEditHref } from '../../../hooks/UseEditHref';
import { useTypedSelector } from '../../../hooks/useTypedSelector';
import './asideWebfacets.scss';
import {
  type WebFacetVmItem,
  selectAsideWebFacetComponentOptions,
  selectAsideWebFacets,
} from './asideWebFacetsSelectors';

const AsideWebFacets: React.FC<{
  config: EditAsideWebFacets;
  readonly: boolean;
}> = ({ config, readonly }) => {
  const editHref = useEditHref();

  const dispatch = useDispatch();
  const confirmationService = getAngularService('ConfirmationService');
  const facetVMs = useTypedSelector((state) => selectAsideWebFacets(state, editHref));
  const facetComponentOptions = useTypedSelector((state) =>
    selectAsideWebFacetComponentOptions(state),
  );

  const facetModalRef = useRef<Modal>();

  useEffect(() => {
    facetVMs.forEach((facetVM) => {
      if ('source' in facetVM.facet && facetVM.sourceName === null) {
        dispatch(loadReferenceFrame({ referenceFrame: facetVM.facet.source.href }));
      }
    });
  }, [facetVMs, dispatch]);

  const addFacet = async () => {
    facetModalRef.current?.open({
      modalTitle: 'Facet toevoegen',
      facetComponentOptions,
      onSubmit: async (facet: Facet) => {
        if (facet) {
          // update facets
          const newFacets = [...facetVMs.map((z) => z.facet), facet];
          dispatch(
            dispatch(updateWebsiteConfigurationActionNew(editHref, config.property, newFacets)),
          );
        }
      },
    });
  };

  const editFacet = async (item: WebFacetVmItem, index: number) => {
    facetModalRef.current?.open({
      facet: item.facet,
      facetComponentOptions,
      modalTitle: 'Facet bewerken',
      onSubmit: async (facet: Facet) => {
        if (facet) {
          const newFacets = facetVMs.map((z) => z.facet);
          newFacets[index] = facet;
          dispatch(
            dispatch(updateWebsiteConfigurationActionNew(editHref, config.property, newFacets)),
          );
        }
      },
    });
  };

  const deleteFacet = async (item: WebFacetVmItem, index) => {
    const confirmed = await confirmationService.confirmDeletion();
    if (confirmed) {
      const newFacets = facetVMs.map((z) => z.facet).filter((_, i) => i !== index);
      dispatch(updateWebsiteConfigurationActionNew(editHref, config.property, newFacets));
    }
  };

  return (
    <div className={'aside-web-facets'}>
      <div className="panel panel-default">
        <div className="panel-heading">
          <div className="panel-controls panel-controls-left">
            <h3 className="panel-title">{config.label}</h3>
          </div>
          <div className="panel-controls panel-controls-right">
            <div className="btn-toolbar">
              {!readonly && (
                <button className="btn btn-default" onClick={addFacet}>
                  <span className="glyphicon glyphicon-add" />
                  <span>Voeg toe</span>
                </button>
              )}
            </div>
          </div>
        </div>

        {facetVMs.length !== 0 && (
          <div className="structure">
            {facetVMs.map((fvm, index) => (
              <div className="row table-row" key={index}>
                <>
                  <div className={'col-md-12 link-row'}>
                    <div>
                      Label: {fvm.facet.label}
                      <br />
                      Component: {fvm.facet.component}
                      {'source' in fvm.facet && (
                        <>
                          <br />
                          Bron:
                          {fvm.sourceName && <>{fvm.sourceName}</>}
                          {!fvm.sourceName && <span className="loadingSlider" />}
                        </>
                      )}
                    </div>
                  </div>
                  {!readonly && (
                    <div
                      className="btn-group btn-group-xs row-actions"
                      role="group"
                      aria-label="..."
                    >
                      <button
                        type="button"
                        className="btn btn-default"
                        onClick={() => editFacet(fvm, index)}
                      >
                        <span className="glyphicon glyphicon-edit" />
                      </button>
                      <button
                        type="button"
                        className="btn btn-default"
                        onClick={() => deleteFacet(fvm, index)}
                      >
                        <span className="glyphicon glyphicon-delete" />
                      </button>
                    </div>
                  )}
                </>
              </div>
            ))}
          </div>
        )}
      </div>

      <Modal
        ref={facetModalRef}
        size="sm"
        component={<FacetEditModal />}
        errorBoundaryComponent={ModalErrorBoundary}
      />
    </div>
  );
};

AsideWebFacets.displayName = 'AsideWebFacets';

export default AsideWebFacets;

AsideWebFacets.propTypes = {
  config: any,
  readonly: any,
};
